import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, AlertTriangle, TrendingUp } from 'lucide-react';

const DemoSection = () => {
  return (
    <section className="py-20 px-4">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            See DisasterConnect in Action
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Experience our comprehensive dashboard with real-time disaster monitoring, 
            community engagement, and emergency response tools.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Dashboard Preview */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-dark-800 to-dark-700 rounded-2xl p-6 border border-dark-600">
              {/* Mock Dashboard Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold">Disaster Dashboard</h3>
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-accent-red rounded-full animate-pulse"></div>
                  <div className="w-3 h-3 bg-accent-orange rounded-full"></div>
                  <div className="w-3 h-3 bg-accent-green rounded-full"></div>
                </div>
              </div>

              {/* Mock Map Area */}
              <div className="bg-dark-900 rounded-lg h-64 mb-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-400/20 to-transparent"></div>
                <div className="absolute top-4 left-4 flex items-center space-x-2 text-sm">
                  <MapPin className="w-4 h-4 text-primary-400" />
                  <span>Global View</span>
                </div>
                
                {/* Mock Disaster Markers */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute top-12 left-20 w-3 h-3 bg-accent-red rounded-full"
                />
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  className="absolute bottom-20 right-16 w-3 h-3 bg-accent-orange rounded-full"
                />
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                  className="absolute top-32 right-32 w-3 h-3 bg-accent-yellow rounded-full"
                />
              </div>

              {/* Mock Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-dark-700 rounded-lg p-3 text-center">
                  <AlertTriangle className="w-5 h-5 text-accent-red mx-auto mb-1" />
                  <div className="text-lg font-bold text-accent-red">3</div>
                  <div className="text-xs text-gray-400">Active</div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3 text-center">
                  <Users className="w-5 h-5 text-primary-400 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-400">1.2k</div>
                  <div className="text-xs text-gray-400">Online</div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3 text-center">
                  <TrendingUp className="w-5 h-5 text-accent-green mx-auto mb-1" />
                  <div className="text-lg font-bold text-accent-green">98%</div>
                  <div className="text-xs text-gray-400">Safe</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Features List */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-400 to-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">Interactive World Map</h4>
                  <p className="text-gray-300">
                    Real-time visualization of disasters worldwide with detailed information, 
                    severity levels, and affected areas.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-accent-green to-primary-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">Community Hub</h4>
                  <p className="text-gray-300">
                    Connect with local communities, share resources, and coordinate 
                    relief efforts during emergencies.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-accent-orange to-accent-red rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">Instant Alerts</h4>
                  <p className="text-gray-300">
                    Receive immediate notifications about disasters in your area 
                    with actionable safety instructions.
                  </p>
                </div>
              </div>
            </div>

            <Link to="/dashboard">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-primary-400 to-primary-500 px-8 py-4 rounded-full text-white font-semibold text-lg hover:shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                Try Dashboard <ArrowRight className="w-5 h-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;